import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, Exam, SignOut, User, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { STATECRAFT_LOGO } from "@/lib/brand";

const NAV = [
    { to: "/dashboard", label: "Dashboard", Icon: House },
    { to: "/mcq?board=kpsc", label: "KPSC", Icon: Exam },
    { to: "/mcq?board=ksp", label: "KSP", Icon: Exam },
    { to: "/mcq?board=kea", label: "KEA", Icon: Exam },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <header className="sticky top-0 z-40 border-b border-[#BFD3FF] glass">
                {/* Top row — centered brand */}
                <div className="max-w-7xl mx-auto px-6 pt-4 pb-3 relative flex items-center justify-center">
                    <button
                        data-testid="brand-link"
                        onClick={() => navigate("/dashboard")}
                        className="flex flex-col items-center gap-1 group"
                    >
                        <img
                            src={STATECRAFT_LOGO}
                            alt="Statecraft IAS Academy"
                            className="h-20 sm:h-24 w-auto object-contain"
                        />
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                            Chintana Portal · <span className="text-[#1E3A8A] font-semibold">KPSC · KEA Prep</span>
                        </div>
                    </button>
                    {/* User area pinned right */}
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-3">
                        {user ? (
                            <>
                                <div className="hidden md:flex items-center gap-2 pr-3 border-r border-[#BFD3FF]">
                                    <Avatar className="w-9 h-9">
                                        <AvatarImage src={user.picture} alt={user.name} />
                                        <AvatarFallback>{(user.name || "U").charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div className="text-xs">
                                        <div className="font-medium text-slate-900 leading-tight">{user.name}</div>
                                        <div className="text-slate-500 leading-tight truncate max-w-[140px]">{user.email}</div>
                                    </div>
                                </div>
                                <button
                                    data-testid="logout-btn"
                                    onClick={logout}
                                    className="p-2 rounded-lg text-slate-500 hover:text-[#1E3A8A] hover:bg-slate-100"
                                    title="Sign out"
                                >
                                    <SignOut size={22} weight="duotone" />
                                </button>
                                {user.is_admin && (
                                    <Link
                                        to="/admin"
                                        data-testid="admin-portal-link"
                                        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-[#1E3A8A]/25 bg-[#1E3A8A]/5 text-[#1E3A8A] text-[10px] font-bold uppercase tracking-widest hover:bg-[#1E3A8A]/10"
                                        title="Admin console"
                                    >
                                        <ShieldCheck size={12} weight="fill" /> Admin
                                    </Link>
                                )}
                            </>
                        ) : (
                            <Link to="/" className="btn-ghost">
                                <User size={16} /> Sign in
                            </Link>
                        )}
                    </div>
                </div>

                {/* Bottom row — nav */}
                <nav className="max-w-7xl mx-auto px-6 pb-3 hidden lg:flex items-center justify-center gap-1 whitespace-nowrap">
                    {NAV.map(({ to, label, Icon }) => {
                        const pathOnly = to.split("?")[0];
                        const board = to.includes("board=") ? to.split("board=")[1] : null;
                        const active = board
                            ? (location.pathname === pathOnly && location.search.includes(`board=${board}`))
                            : location.pathname === pathOnly;
                        const testId = board ? `nav-${board}` : `nav-${pathOnly.slice(1)}`;
                        return (
                            <Link
                                key={to}
                                to={to}
                                data-testid={testId}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    active ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                }`}
                            >
                                <Icon size={17} weight={active ? "duotone" : "regular"} />
                                {label}
                            </Link>
                        );
                    })}
                </nav>

                {/* Mobile nav */}
                <nav className="lg:hidden flex items-center gap-1 px-3 pb-3 overflow-x-auto">
                    {NAV.map(({ to, label, Icon }) => {
                        const pathOnly = to.split("?")[0];
                        const board = to.includes("board=") ? to.split("board=")[1] : null;
                        const active = board
                            ? (location.pathname === pathOnly && location.search.includes(`board=${board}`))
                            : location.pathname === pathOnly;
                        return (
                            <Link
                                key={to}
                                to={to}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${
                                    active ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "text-slate-600"
                                }`}
                            >
                                <Icon size={16} weight="duotone" /> {label}
                            </Link>
                        );
                    })}
                </nav>
            </header>
            <main className="max-w-7xl mx-auto px-6 py-8">
                {location.pathname !== "/dashboard" && (
                    <div className="mb-6">
                        <button
                            data-testid="global-back-btn"
                            onClick={() => {
                                if (window.history.length > 1) navigate(-1);
                                else navigate("/dashboard");
                            }}
                            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#1E3A8A] transition-colors group"
                        >
                            <span className="w-8 h-8 grid place-items-center rounded-full border border-[#BFD3FF] bg-white group-hover:border-[#1E3A8A] group-hover:bg-[#1E3A8A]/5 transition-colors">
                                <ArrowLeft size={16} weight="bold" />
                            </span>
                            Back
                        </button>
                    </div>
                )}
                {children}
            </main>
            <footer className="border-t border-[#BFD3FF] mt-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <img src={STATECRAFT_LOGO} alt="Statecraft" className="h-10 w-auto" />
                        <div className="text-xs text-slate-500">© Statecraft IAS Academy · Crafting the path to success</div>
                    </div>
                    <div className="text-xs text-slate-500">Chintana · Purpose-built for KPSC & KEA aspirants</div>
                </div>
            </footer>
        </div>
    );
}
